import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Users, MousePointerClick, UserCheck, MessageSquare, Globe, PenTool, Moon, ArrowRight, Calendar } from 'lucide-react';

const AdditionalServices = ({ navigateToHome }) => {
  const openCalendly = () => {
    window.open('https://calendly.com/shah-eclipseer/30-minute-demo', '_blank', 'noopener,noreferrer');
  };

  const services = [
    {
      title: 'Playtests',
      desc: 'Watch real people interact with your product. Discover excitement, engagement and improvement opportunities.',
      icon: <Gamepad2 size={32} />
    },
    {
      title: 'Interviews',
      desc: 'Conversations to uncover what customers think, need and care about most.',
      icon: <Users size={32} />
    },
    {
      title: 'Usability Tests',
      desc: 'See if your product feels natural to use. Catch pain points early, before they scale.',
      icon: <MousePointerClick size={32} />
    },
    {
      title: 'Expert Reviews',
      desc: 'UX insights and recommendations delivered fast, clear and aligned to your goals.',
      icon: <UserCheck size={32} />
    },
    {
      title: 'Focus Groups',
      desc: 'Group discussions that test reactions, capture perspectives and inspire new ideas.',
      icon: <MessageSquare size={32} />
    },
    {
      title: 'Social Listening',
      desc: 'Customer opinions and trends surfaced from public conversations.',
      icon: <Globe size={32} />
    },
    {
      title: 'Custom Studies',
      desc: 'Research tailored to your product stage and business outcomes.',
      icon: <PenTool size={32} />
    },
    {
      title: 'Eclipseer Platform',
      desc: 'AI powered surveys and instant insights for confident business decisions.',
      icon: <Moon size={32} fill="currentColor" />,
      isPlatform: true
    },
  ];

  return (
    <div className="bg-black/80 min-h-screen text-white relative">

      {/* --- HERO SECTION --- */}
      <section className="relative pt-40 pb-24 px-6 flex flex-col items-center overflow-hidden min-h-[70vh]">
        {/* Clean Black Background - No canvas or glow */}

        <div className="container mx-auto px-6 text-center z-10 relative pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto pointer-events-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-10 leading-[1.1]">
              User Research <br className="hidden md:block" />
              <span className="relative inline-block px-4 mx-2">
                <span className="absolute inset-0 bg-[#9747FF] opacity-40 -skew-x-6 rounded-lg transform rotate-1"></span>
                <span className="relative z-10">Made Simple.</span>
              </span>
              <br />
              <span style={{ color: '#ffffff' }} className="block mt-4 text-3xl md:text-5xl">
                We Do the Work, You Get the Answers.
              </span>
            </h1>

            <p className="text-gray-400 text-lg md:text-xl mb-12 max-w-5xl mx-auto leading-relaxed">
              From playtests to focus groups, our expert team and platform run the research for you, delivering clear, customer-led insights your business can act on.
            </p>

            <button
              onClick={openCalendly}
              className="bg-[#9747FF] hover:bg-[#833de0] text-white px-10 py-5 rounded-xl font-bold text-xl tracking-wide transition-all shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 hover:-translate-y-1 flex items-center gap-3 mx-auto group"
            >
              Contact Us
              <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* --- GRID SECTION --- */}
      <section className="container mx-auto px-6 mb-24 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              onClick={service.isPlatform ? navigateToHome : undefined}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className={`
                group relative p-8 rounded-3xl cursor-pointer transition-all duration-300 flex flex-col items-center text-center min-h-[340px] justify-start border-slate-400/15
                ${service.isPlatform
                  ? 'bg-[#1A0B2E] border-[#9747FF]/40 hover:shadow-[0_0_40px_rgba(151,71,255,0.4)] hover:scale-[1.02]'
                  : 'bg-[#D9D9D9] text-black hover:bg-[#9747FF] hover:text-white'
                }
              `}
            >
              {/* Icon */}
              <div className={`
                mb-6 p-4 rounded-2xl transition-colors duration-300 shadow-lg
                ${service.isPlatform
                  ? 'bg-[#9747FF] text-white'
                  : 'bg-black text-white group-hover:bg-white group-hover:text-[#9747FF]'
                }
              `}>
                {service.icon}
              </div>

              {/* Text */}
              <h3 className={`text-2xl font-display font-bold mb-4 ${service.isPlatform ? 'text-[#9747FF]' : ''} group-hover:text-white`}>
                {service.title}
              </h3>
              <p className={`text-sm leading-relaxed font-medium ${service.isPlatform ? 'text-gray-300' : 'text-gray-700'} group-hover:text-white/90`}>
                {service.desc}
              </p>
              {service.isPlatform && (
                <div className="mt-4 text-[#9747FF] font-bold text-sm flex items-center gap-2 group-hover:text-white transition-colors">
                  Learn more <ArrowRight size={16} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* --- EASY STEPS SECTION --- */}
      <section className="container mx-auto px-6 mb-24 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-20 leading-tight">
            You’re 3 Steps Away from <br />
            <span className="text-[#9747FF]">Smarter Customer-Led Decisions</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-12 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-1 bg-[#1A1A1A] z-0"></div>

            {[
              { id: 1, text: "Contact us to book a short consultation call" },
              { id: 2, text: "We deliver clear, actionable insights" },
              { id: 3, text: "You make confident business decisions" }
            ].map((step, idx) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="relative z-10 flex flex-col items-center"
              >
                <div className="w-20 h-20 rounded-full bg-[#9747FF] text-white text-3xl font-bold flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(151,71,255,0.4)] border-4 border-black transition-transform hover:scale-110">
                  {step.id}
                </div>
                <p className="text-xl font-medium max-w-xs">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* --- TRUST & PERSUASION SECTION --- */}
      <section className="container mx-auto px-6 text-center pb-24">
        <div className="bg-[#121212] border-slate-400/15 rounded-[3rem] p-12 md:p-24 relative overflow-hidden">
          {/* Background Glow removed as requested */}

          <div className="relative z-10">
            <h3 className="text-2xl md:text-5xl font-display font-bold mb-12 max-w-4xl mx-auto leading-snug">
              “Research support designed for teams of all sizes and every business stage”
            </h3>

            <button
              onClick={openCalendly}
              className="bg-[#9747FF] hover:bg-[#833de0] text-white px-12 py-5 rounded-xl font-bold text-xl tracking-wide transition-all shadow-xl shadow-purple-900/20 hover:shadow-purple-900/40 hover:-translate-y-1 flex items-center gap-3 mx-auto"
            >
              <Calendar size={24} />
              Contact Us
            </button>
            <p className="mt-6 text-gray-500 text-sm">
              Schedule a call to discuss your specific needs
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AdditionalServices;
